import React, { useState, useEffect, useRef, useCallback } from 'react';
import { chatAPI } from '../services/api';
import { ChatResponse, ChatMessage, ChatQuestion, APIError } from '../types';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: {
    id: number;
    filename: string;
    relevance_score: number;
  }[];
}

/** Questions pulled per page of the history list. */
const QUESTIONS_PAGE_SIZE = 30;

type Tab = 'chat' | 'history';

/**
 * Answers are fetched one at a time as questions are opened.
 * `null` marks a fetch that failed, so the row can offer a retry instead of
 * spinning forever — an entry that is simply absent has never been requested.
 */
type AnswerCache = Record<number, ChatMessage | null>;

const formatTimestamp = (date: Date) => {
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (date.toDateString() === new Date().toDateString()) return time;
  // Saved questions can be weeks old — date them so they read in order
  return `${date.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' })}, ${time}`;
};

const Spinner: React.FC<{ className?: string }> = ({ className = 'h-5 w-5 text-bmlc-gold-500' }) => (
  <svg className={`animate-spin ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const Chat: React.FC = () => {
  const [tab, setTab] = useState<Tab>('chat');

  /* ---------------- live conversation ---------------- */
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  /* ---------------- saved history ---------------- */
  const [questions, setQuestions] = useState<ChatQuestion[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [moreLoading, setMoreLoading] = useState(false);
  const [questionsPage, setQuestionsPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [search, setSearch] = useState('');
  // Committed search term, so typing doesn't fire a request per keystroke
  const [activeSearch, setActiveSearch] = useState('');
  const [historyError, setHistoryError] = useState('');
  // Bumped to invalidate the list: History reopened, or a new question asked
  const [refreshKey, setRefreshKey] = useState(0);
  const [openId, setOpenId] = useState<number | null>(null);
  const [answers, setAnswers] = useState<AnswerCache>({});
  const [answerLoadingId, setAnswerLoadingId] = useState<number | null>(null);
  // Only the newest list request may write to state — a search committed while
  // an earlier fetch is in flight must not be overwritten by that fetch
  const requestSeqRef = useRef(0);

  useEffect(() => {
    if (tab !== 'chat') return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, tab]);

  const fetchQuestions = useCallback(async (page: number, term: string, mode: 'replace' | 'append') => {
    const seq = ++requestSeqRef.current;
    try {
      if (mode === 'replace') {
        setQuestionsLoading(true);
      } else {
        setMoreLoading(true);
      }
      setHistoryError('');

      const response = await chatAPI.getQuestions(page, QUESTIONS_PAGE_SIZE, term);
      if (seq !== requestSeqRef.current) return;

      setQuestions(prev => {
        if (mode === 'replace') return response.questions;
        // Questions asked since the list loaded shift the OFFSET, so a later
        // page can repeat rows already listed — drop those
        const known = new Set(prev.map(item => item.id));
        return [...prev, ...response.questions.filter(item => !known.has(item.id))];
      });
      setQuestionsPage(page);
      setHasMore(Boolean(response.pagination?.has_next));
      setTotalQuestions(response.pagination?.total_questions ?? response.questions.length);
    } catch (err: any) {
      if (seq !== requestSeqRef.current) return;
      const apiError = err.response?.data as APIError;
      setHistoryError(apiError?.error || 'Could not load your questions.');
    } finally {
      // A superseded request leaves the spinner to the request that replaced it
      if (seq === requestSeqRef.current) {
        setQuestionsLoading(false);
        setMoreLoading(false);
      }
    }
  }, []);

  // Runs on mount so the History count is correct from the first paint, then
  // again whenever a search is committed or the list is invalidated
  useEffect(() => {
    fetchQuestions(1, activeSearch, 'replace');
  }, [activeSearch, refreshKey, fetchQuestions]);

  const openHistory = () => {
    setOpenId(null);
    // Pick up anything asked since the list was last loaded
    setRefreshKey(key => key + 1);
    setTab('history');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setActiveSearch(search.trim());
  };

  const loadAnswer = useCallback(async (id: number) => {
    try {
      setAnswerLoadingId(id);
      const { message } = await chatAPI.getHistoryItem(id);
      setAnswers(prev => ({ ...prev, [id]: message }));
    } catch (err: any) {
      // Mark the failure so the row shows a retry rather than an empty panel
      setAnswers(prev => ({ ...prev, [id]: null }));
    } finally {
      setAnswerLoadingId(null);
    }
  }, []);

  const handleToggleQuestion = (id: number) => {
    if (openId === id) {
      setOpenId(null);
      return;
    }
    setOpenId(id);
    // Answers are cached for the session; only fetch what we don't have
    if (answers[id] === undefined || answers[id] === null) {
      loadAnswer(id);
    }
  };

  const handleAskAgain = (question: string) => {
    setInput(question);
    setTab('chat');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setTab('chat');
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setError('');

    try {
      const response: ChatResponse = await chatAPI.ask(userMessage.content);

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: response.answer,
        timestamp: new Date(),
        sources: response.sources,
      };

      setMessages(prev => [...prev, assistantMessage]);
      // The answer is now saved — invalidate the history list
      setRefreshKey(key => key + 1);
    } catch (err: any) {
      const apiError = err.response?.data as APIError;
      setError(apiError?.error || 'Failed to get response');

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const tabButton = (value: Tab, label: string, count?: number) => {
    const active = tab === value;
    return (
      <button
        type="button"
        onClick={() => (value === 'history' ? openHistory() : setTab('chat'))}
        aria-current={active ? 'page' : undefined}
        className={`px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all duration-200 ease-out-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-bmlc-gold-400 ${
          active
            ? 'bg-navy-sheen text-white shadow-soft-sm'
            : 'bg-white text-bmlc-navy-700 border border-bmlc-navy-100 hover:border-bmlc-gold-400 hover:bg-bmlc-gold-50'
        }`}
      >
        {label}
        {typeof count === 'number' && count > 0 && (
          <span className={`ml-2 ${active ? 'text-bmlc-gold-300' : 'text-gray-400'}`}>{count}</span>
        )}
      </button>
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] sm:h-[calc(100vh-5rem)] md:h-[calc(100vh-6rem)]">
      {/* Header */}
      <div className="page-hero mb-3 sm:mb-4 animate-fade-in">
        <div className="relative z-10 flex items-center gap-3 sm:gap-4">
          <div className="flex-shrink-0 w-14 h-14 sm:w-[4.5rem] sm:h-[4.5rem] rounded-full bg-white p-1 ring-2 ring-bmlc-gold-500/50 shadow-soft-sm overflow-hidden">
            <img
              src="https://edifywebsite.blob.core.windows.net/edify-files/IMG_9812.JPG"
              alt="BM Law College Logo"
              className="w-full h-full object-contain rounded-full"
            />
          </div>
          <div className="min-w-0">
            <h1 className="font-serif text-xl sm:text-2xl md:text-3xl font-bold text-white leading-tight">Legal AI Assistant</h1>
            <p className="text-[11px] sm:text-sm font-semibold text-bmlc-gold-300 tracking-wide uppercase">BM Law College Research Platform</p>
            <p className="mt-1 text-[11px] sm:text-sm text-white/70 leading-snug max-w-2xl">
              Ask questions about legal documents, case laws, or any legal topic. Every question you ask is saved under History.
            </p>
          </div>
        </div>
      </div>

      {/* Chat / History switch */}
      <div className="flex items-center gap-2 mb-3 sm:mb-4">
        {tabButton('chat', 'Chat')}
        {tabButton('history', 'History', totalQuestions)}
      </div>

      {error && tab === 'chat' && (
        <div className="toast-error mb-4 animate-fade-in">
          <span className="text-sm sm:text-base">⚠️</span>
          <p className="text-xs sm:text-sm">{error}</p>
        </div>
      )}

      {historyError && tab === 'history' && (
        <div className="toast-error mb-4 animate-fade-in">
          <span className="text-sm sm:text-base">⚠️</span>
          <p className="text-xs sm:text-sm">{historyError}</p>
        </div>
      )}

      {/* ------------------------------- CHAT ------------------------------- */}
      {tab === 'chat' && (
        <div className="flex-1 card-premium overflow-hidden">
          <div className="messages-container p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
            {messages.length === 0 ? (
              <div className="text-center py-8 sm:py-12 animate-slide-up">
                <div className="mb-4 sm:mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-bmlc-gold-50 to-bmlc-gold-100 ring-1 ring-bmlc-gold-300/60 shadow-gold-glow mb-4">
                    <span className="text-3xl sm:text-4xl md:text-5xl">🤖</span>
                  </div>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-bmlc-navy-900 mb-2">Welcome to Legal AI Assistant</h3>
                <div className="divider-gold w-24 mx-auto my-3"></div>
                <p className="text-gray-600 text-sm sm:text-base mb-4 sm:mb-6 max-w-md mx-auto px-4">
                  I'm here to help you with legal research, case laws, and any legal questions. Ask me anything!
                </p>
                <div className="flex flex-wrap justify-center gap-2 sm:gap-3 max-w-2xl mx-auto px-4">
                  {[
                    "What is contract law?",
                    "Explain tort law principles",
                    "Recent Supreme Court judgments",
                    "Intellectual property rights"
                  ].map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => setInput(suggestion)}
                      className="px-3 py-1.5 sm:px-4 sm:py-2 bg-white border border-bmlc-navy-100 text-bmlc-navy-700 rounded-full text-xs sm:text-sm font-medium shadow-soft-sm hover:border-bmlc-gold-400 hover:bg-bmlc-gold-50 hover:text-bmlc-navy-900 hover:-translate-y-0.5 transition-all duration-200 ease-out-soft"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
                {totalQuestions > 0 && (
                  <button
                    type="button"
                    onClick={openHistory}
                    className="mt-6 text-xs sm:text-sm font-semibold text-bmlc-navy-700 underline decoration-bmlc-gold-400 decoration-2 underline-offset-4 hover:text-bmlc-navy-900"
                  >
                    View your {totalQuestions} saved question{totalQuestions === 1 ? '' : 's'}
                  </button>
                )}
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} ${message.type === 'user' ? 'message-user' : 'message-assistant'}`}
                >
                  <div className={`flex items-start space-x-2 sm:space-x-3 max-w-[85%] sm:max-w-[80%] ${message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                    {/* Avatar */}
                    <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full ring-2 ring-white shadow-soft-sm flex items-center justify-center text-sm sm:text-base ${
                      message.type === 'user'
                        ? 'bg-gradient-to-br from-bmlc-gold-400 to-bmlc-gold-600 text-white'
                        : 'bg-navy-sheen text-white'
                    }`}>
                      {message.type === 'user' ? '👤' : '⚖️'}
                    </div>

                    {/* Message Bubble */}
                    <div
                      className={`message-bubble px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 rounded-2xl ${
                        message.type === 'user'
                          ? 'rounded-tr-md bg-navy-sheen text-white'
                          : 'rounded-tl-md bg-white text-gray-900 border border-bmlc-navy-100 border-l-[3px] border-l-bmlc-gold-500'
                      }`}
                    >
                      <p className="whitespace-pre-wrap leading-relaxed text-xs sm:text-sm">{message.content}</p>

                      {message.sources && message.sources.length > 0 && (
                        <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-bmlc-navy-100">
                          <div className="flex items-center space-x-2 mb-1 sm:mb-2">
                            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-bmlc-gold-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-bmlc-navy-700">Sources:</p>
                          </div>
                          {message.sources.map((source, index) => (
                            <div key={index} className="flex items-center space-x-2 text-[10px] sm:text-xs text-gray-600">
                              <span className="w-1.5 h-1.5 bg-bmlc-gold-500 rounded-full"></span>
                              <span>{source.filename}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <p className={`text-[10px] sm:text-xs mt-1.5 sm:mt-2 flex items-center space-x-1 ${
                        message.type === 'user' ? 'text-white/70' : 'text-gray-400'
                      }`}>
                        <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{formatTimestamp(message.timestamp)}</span>
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}

            {loading && (
              <div className="flex justify-start animate-fade-in">
                <div className="flex items-start space-x-2 sm:space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full ring-2 ring-white shadow-soft-sm bg-navy-sheen text-white flex items-center justify-center text-sm sm:text-base">
                    ⚖️
                  </div>
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      )}

      {/* ------------------------------ HISTORY ----------------------------- */}
      {tab === 'history' && (
        <div className="flex-1 card-premium overflow-hidden flex flex-col">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex items-center gap-2 p-3 sm:p-4 border-b border-bmlc-navy-100">
            <div className="flex-1 min-w-0">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search your questions..."
                className="input-premium px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl text-xs sm:text-sm"
              />
            </div>
            <button
              type="submit"
              className="btn-secondary flex-shrink-0 px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-semibold whitespace-nowrap"
            >
              Search
            </button>
            {activeSearch && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setActiveSearch('');
                }}
                className="btn-ghost flex-shrink-0 px-3 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-semibold text-gray-500 whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </form>

          <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 md:p-5 space-y-2 sm:space-y-3">
            {questionsLoading && questions.length > 0 && (
              <p className="text-center text-[11px] sm:text-xs text-gray-400 pb-1">Updating…</p>
            )}

            {questionsLoading && questions.length === 0 ? (
              <div className="text-center py-10 animate-fade-in">
                <Spinner className="h-7 w-7 mx-auto text-bmlc-gold-500" />
                <p className="mt-3 text-xs sm:text-sm text-gray-500">Loading your questions…</p>
              </div>
            ) : questions.length === 0 ? (
              <div className="text-center py-10 sm:py-14 animate-slide-up px-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-bmlc-gold-50 to-bmlc-gold-100 ring-1 ring-bmlc-gold-300/60 mb-4">
                  <svg className="w-7 h-7 text-bmlc-gold-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                {activeSearch ? (
                  <>
                    <h3 className="font-serif text-lg sm:text-xl font-bold text-bmlc-navy-900 mb-2">No matching questions</h3>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      Nothing in your history matches “{activeSearch}”.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="font-serif text-lg sm:text-xl font-bold text-bmlc-navy-900 mb-2">No questions yet</h3>
                    <p className="text-gray-600 text-xs sm:text-sm mb-4">
                      Every question you ask will be saved here, so you can come back to the answers any time.
                    </p>
                    <button
                      type="button"
                      onClick={() => setTab('chat')}
                      className="btn-gold px-5 py-2 rounded-xl text-white text-xs sm:text-sm font-semibold"
                    >
                      Ask your first question
                    </button>
                  </>
                )}
              </div>
            ) : (
              <>
                {questions.map((item) => {
                  const isOpen = openId === item.id;
                  const answer = answers[item.id];
                  const isAnswerLoading = answerLoadingId === item.id;

                  return (
                    <div
                      key={item.id}
                      className={`rounded-xl border bg-white transition-all duration-200 ease-out-soft ${
                        isOpen
                          ? 'border-bmlc-gold-400 shadow-soft-sm'
                          : 'border-bmlc-navy-100 hover:border-bmlc-gold-400'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => handleToggleQuestion(item.id)}
                        aria-expanded={isOpen}
                        className="w-full flex items-start gap-3 p-3 sm:p-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-bmlc-gold-400 rounded-xl"
                      >
                        <span className="flex-shrink-0 mt-0.5 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-bmlc-gold-400 to-bmlc-gold-600 text-white flex items-center justify-center text-[11px] sm:text-xs font-bold">
                          Q
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-xs sm:text-sm font-semibold text-bmlc-navy-900 break-words">
                            {item.question}
                          </span>
                          <span className="block mt-1 text-[10px] sm:text-xs text-gray-400">
                            {formatTimestamp(new Date(item.created_at))}
                          </span>
                        </span>
                        <svg
                          className={`flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5 text-bmlc-navy-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {isOpen && (
                        <div className="px-3 sm:px-4 pb-3 sm:pb-4 animate-fade-in">
                          <div className="border-t border-bmlc-navy-100 pt-3">
                            {isAnswerLoading ? (
                              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                                <Spinner className="h-4 w-4 text-bmlc-gold-500" />
                                <span>Loading the answer…</span>
                              </div>
                            ) : answer === null ? (
                              <div className="flex items-center gap-3">
                                <p className="text-xs sm:text-sm text-bmlc-maroon-600">Could not load this answer.</p>
                                <button
                                  type="button"
                                  onClick={() => loadAnswer(item.id)}
                                  className="btn-secondary px-3 py-1 rounded-lg text-[11px] sm:text-xs font-semibold"
                                >
                                  Retry
                                </button>
                              </div>
                            ) : answer ? (
                              <>
                                <div className="flex items-start gap-2 sm:gap-3">
                                  <span className="flex-shrink-0 mt-0.5 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-navy-sheen text-white flex items-center justify-center text-sm">
                                    ⚖️
                                  </span>
                                  <p className="min-w-0 flex-1 whitespace-pre-wrap leading-relaxed text-xs sm:text-sm text-gray-900">
                                    {answer.answer}
                                  </p>
                                </div>
                                <div className="mt-3 flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => handleAskAgain(item.question)}
                                    className="btn-secondary px-3 py-1.5 rounded-lg text-[11px] sm:text-xs font-semibold"
                                  >
                                    Ask again
                                  </button>
                                </div>
                              </>
                            ) : null}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {hasMore && (
                  <div className="flex justify-center pt-2">
                    <button
                      type="button"
                      onClick={() => fetchQuestions(questionsPage + 1, activeSearch, 'append')}
                      disabled={moreLoading}
                      className="btn-secondary px-4 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {moreLoading ? 'Loading…' : 'Load more questions'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Input Form */}
      <form onSubmit={handleSubmit} className="mt-3 sm:mt-4 flex items-center gap-2 sm:gap-3 animate-slide-up">
        <div className="flex-1 relative">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about legal topics, case laws, or documents..."
            className="input-premium px-4 py-2.5 sm:px-6 sm:py-4 pr-10 sm:pr-12 rounded-2xl shadow-soft-sm text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={loading}
          />
          <div className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-bmlc-navy-300 pointer-events-none">
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
        </div>
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="btn-gold px-4 py-2.5 sm:px-6 sm:py-4 rounded-2xl text-white text-xs sm:text-sm font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          {loading ? (
            <>
              <Spinner className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              <span>Sending...</span>
            </>
          ) : (
            <>
              <span>Send</span>
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default Chat;
